import { useState, useEffect } from 'react';

// Demo data for testing without API
const DEMO_DATA = {
  brands: {
    "McDonald's": "MC001",
    "Burger King": "BK001",
    "Wendy's": "WD001",
    "Subway": "SB001"
  },
  stores: {
    "McDonald's": [
      { storeName: "Downtown McDonald's", regionName: "Downtown" },
      { storeName: "Uptown McDonald's", regionName: "Uptown" },
      { storeName: "Mall McDonald's", regionName: "Mall" }
    ],
    "Burger King": [
      { storeName: "Main Street BK", regionName: "Main Street" },
      { storeName: "Highway BK", regionName: "Highway" }
    ],
    "Wendy's": [
      { storeName: "Central Wendy's", regionName: "Central" },
      { storeName: "North Wendy's", regionName: "North" }
    ],
    "Subway": [
      { storeName: "Downtown Subway", regionName: "Downtown" },
      { storeName: "University Subway", regionName: "University" }
    ]
  },
  products: [
    { name: "Hamburger", creditCost: 2.50 },
    { name: "Cheeseburger", creditCost: 3.00 },
    { name: "French Fries", creditCost: 1.75 },
    { name: "Chicken Sandwich", creditCost: 4.25 },
    { name: "Fish Sandwich", creditCost: 4.50 },
    { name: "Soda", creditCost: 1.25 },
    { name: "Milkshake", creditCost: 3.50 },
    { name: "Ice Cream", creditCost: 2.00 }
  ]
};

export function useGoogleSheets() {
  const [brands, setBrands] = useState({});
  const [stores, setStores] = useState({});
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Call Netlify function to get data
  const fetchDataFromFunction = async () => {
    try {
      const response = await fetch('/.netlify/functions/google-sheets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getData'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw new Error(`Function call failed: ${err.message}`);
    }
  };

  // Load all data from Google Sheets via Netlify function
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await fetchDataFromFunction();
      
      setBrands(data.brands);
      setStores(data.stores);
      setProducts(data.products);
      setIsDemoMode(false);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data from Google Sheets');
    } finally {
      setIsLoading(false);
    }
  };

  // Load demo data
  const loadDemoData = () => {
    setIsLoading(true);
    setTimeout(() => {
      setBrands(DEMO_DATA.brands);
      setStores(DEMO_DATA.stores);
      setProducts(DEMO_DATA.products);
      setIsDemoMode(true);
      setIsLoading(false);
      setError(null);
    }, 1000); // Simulate loading time
  };

  // Submit data via Netlify function
  const submitData = async (rows, userEmail) => {
    if (isDemoMode) {
      // In demo mode, just simulate submission
      setIsSubmitting(true);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      setIsSubmitting(false);
      console.log('Demo mode - Data would be submitted:', rows, 'User email:', userEmail);
      return { success: true };
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('/.netlify/functions/google-sheets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'submitData',
          data: {
            rows: rows,
            userEmail: userEmail
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit data');
      }

      return await response.json();
    } catch (err) {
      console.error('Error submitting data:', err);
      throw new Error(err.message || 'Failed to submit data to Google Sheets');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    // Try to load real data first, fall back to demo if it fails
    loadData().catch(() => {
      console.log('Falling back to demo mode');
      loadDemoData();
    });
  }, []);

  return {
    brands,
    stores,
    products,
    isLoading,
    error,
    submitData,
    isSubmitting,
    isDemoMode,
    reloadData: loadData,
    loadDemoData
  };
}
